// QS-Meldungen Liste + Detail + Bearbeiten
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, type ProblemMeldung, type ProblemStatus } from '../lib/api';

const MODULE_LABELS: Record<string, string> = {
  drueckteile: 'Drückteile', laufraeder: 'Laufräder',
  baugruppen: 'Baugruppen', montage: 'Montage',
};

const KATEGORIE_ICONS: Record<string, string> = {
  material: '🧱', masse: '📐', zeichnung: '📋',
  oberflaeche: '🎨', maschine: '⚙️', sonstige: '❓',
};

const STATUS_COLORS: Record<ProblemStatus, { bg: string; text: string; label: string }> = {
  offen:          { bg: 'bg-red-100', text: 'text-red-800', label: 'Offen' },
  in_bearbeitung: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'In Bearbeitung' },
  geloest:        { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Gelöst' },
  abgelehnt:      { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Abgelehnt' },
};

export function QS() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<'' | ProblemStatus>('offen');
  const [selected, setSelected] = useState<ProblemMeldung | null>(null);

  const list = useApi(() => api.problems.list({
    status: statusFilter || undefined, limit: 200,
  }), [statusFilter]);
  const count = useApi(() => api.problems.count(), []);

  // URL'deki ?id=X → o meldungu modal olarak aç
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    const numId = parseInt(id);
    if (isNaN(numId)) return;
    api.problems.get(numId)
      .then(r => setSelected(r.data))
      .catch(() => {/* ignore */});
  }, [searchParams]);

  const closeModal = () => {
    setSelected(null);
    if (searchParams.has('id')) {
      searchParams.delete('id');
      setSearchParams(searchParams, { replace: true });
    }
  };

  useEffect(() => {
    const doReload = () => { list.reload(); count.reload(); };
    const t = setInterval(doReload, 5_000);
    const onVis = () => { if (document.visibilityState === 'visible') doReload(); };
    const onFocus = () => doReload();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  return (
    <div className="p-8">
      <header className="mb-6">
        <div className="text-xs uppercase tracking-wider text-slate-500">Qualitätssicherung</div>
        <h1 className="text-2xl font-bold text-slate-900">QS-Meldungen</h1>
      </header>

      {/* Status Summary */}
      {count.data && (
        <div className="grid grid-cols-5 gap-3 mb-4">
          <StatusBox label="Offen"          value={count.data.data.offen}          tone="red" />
          <StatusBox label="In Bearbeitung" value={count.data.data.in_bearbeitung} tone="amber" />
          <StatusBox label="Heute neu"      value={count.data.data.heute_neu}      tone="sky" />
          <StatusBox label="Heute gelöst"   value={count.data.data.heute_geloest}  tone="emerald" />
          <StatusBox label="Kritisch offen" value={count.data.data.kritisch}       tone={count.data.data.kritisch > 0 ? 'red' : 'slate'} />
        </div>
      )}

      {/* Filter */}
      <div className="bg-white rounded-xl p-3 border border-slate-200 mb-4 flex items-center gap-3">
        <span className="text-sm text-slate-600">Status:</span>
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {(['', 'offen','in_bearbeitung','geloest','abgelehnt'] as const).map(s => (
            <button key={s} onClick={() => setStatusFilter(s as '' | ProblemStatus)}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${
                      statusFilter === s ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}>
              {s === '' ? 'Alle' : STATUS_COLORS[s as ProblemStatus].label}
            </button>
          ))}
        </div>
        <div className="ml-auto text-sm text-slate-500">
          {list.data?.data.length ?? 0} Meldungen
        </div>
      </div>

      <div className="space-y-2">
        {list.loading && <div className="text-slate-500">Lädt …</div>}
        {list.data && list.data.data.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
            Keine Meldungen in dieser Kategorie
          </div>
        )}
        {list.data?.data.map(p => (
          <div key={p.id}
               onClick={() => setSelected(p)}
               className={`bg-white rounded-xl border p-3 cursor-pointer transition hover:shadow-md hover:border-brand ${
                 p.priority === 'kritisch' ? 'border-l-4 border-l-red-600' : 'border-slate-200'
               }`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-lg">{KATEGORIE_ICONS[p.kategorie]}</span>
                <span className="font-semibold capitalize">{p.kategorie}</span>
                {p.priority !== 'normal' && (
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                    p.priority === 'kritisch' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
                  }`}>{p.priority}</span>
                )}
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_COLORS[p.status].bg} ${STATUS_COLORS[p.status].text}`}>
                {STATUS_COLORS[p.status].label}
              </span>
            </div>
            <div className="text-sm">
              <span className="font-medium">{p.auftrag?.kunde_name ?? '—'}</span>
              <span className="text-slate-500"> · {p.auftrag?.bestellnr} · {MODULE_LABELS[p.module_table]}</span>
            </div>
            <div className="text-xs text-slate-600 mt-1 truncate">{p.beschreibung ?? '—'}</div>
            <div className="text-xs text-slate-400 mt-1 flex items-center justify-between">
              <span>{(p.gemeldet_von_name ?? '').replace(/^\[WS\]\s*/, '')} · {formatDateTime(p.gemeldet_am)}</span>
              {p.abteilung_name && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold"
                      style={{ background: `${p.abteilung_farbe}30`, color: p.abteilung_farbe ?? '' }}>
                  {p.abteilung_name}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => setSelected(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto"
               onClick={e => e.stopPropagation()}>
            <QSDetail meldung={selected} onUpdated={() => { list.reload(); count.reload(); }}
                      onClose={closeModal} />
          </div>
        </div>
      )}
    </div>
  );
}

function QSDetail({ meldung, onUpdated, onClose }: {
  meldung: ProblemMeldung; onUpdated: () => void; onClose: () => void;
}) {
  const [loesung, setLoesung] = useState(meldung.loesung ?? '');
  const [saving, setSaving] = useState(false);

  // Meldung id değişirse loesung'u sync et
  useEffect(() => { setLoesung(meldung.loesung ?? ''); }, [meldung.id, meldung.loesung]);

  const update = async (patch: Partial<ProblemMeldung>) => {
    setSaving(true);
    try {
      await api.problems.update(meldung.id, patch);
      onUpdated();
      if (patch.status === 'geloest' || patch.status === 'abgelehnt') onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs uppercase text-red-700 font-bold">⚠ Meldung #{meldung.id}</span>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${STATUS_COLORS[meldung.status].bg} ${STATUS_COLORS[meldung.status].text}`}>
              {STATUS_COLORS[meldung.status].label}
            </span>
            {meldung.priority !== 'normal' && (
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                meldung.priority === 'kritisch' ? 'bg-red-600 text-white' : 'bg-orange-500 text-white'
              }`}>{meldung.priority}</span>
            )}
          </div>
          <h3 className="text-2xl font-bold flex items-center gap-2">
            <span>{KATEGORIE_ICONS[meldung.kategorie]}</span>
            <span className="capitalize">{meldung.kategorie}</span>
          </h3>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
      </div>

      <div className="bg-slate-50 rounded p-3 mb-4 text-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-semibold text-base">{meldung.auftrag?.kunde_name ?? '—'}</div>
            <div className="text-xs text-slate-500">{meldung.auftrag?.bestellnr} · DV {meldung.auftrag?.dv_nummer ?? '—'} · {MODULE_LABELS[meldung.module_table]}</div>
          </div>
          <Link to={`/${meldung.module_table}/${meldung.record_id}`}
                className="text-xs text-brand hover:underline">
            Auftrag öffnen →
          </Link>
        </div>
        <div className="text-sm mt-2 text-slate-700">{meldung.auftrag?.bezeichnung ?? '—'}</div>
        {meldung.auftrag?.lieferdatum && (
          <div className="text-xs text-slate-500 mt-1">Liefertermin: {new Date(meldung.auftrag.lieferdatum).toLocaleDateString('de-DE')}</div>
        )}
      </div>

      {meldung.beschreibung && (
        <>
          <label className="text-xs font-semibold text-slate-600">Problem-Beschreibung</label>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm mt-1 mb-4 whitespace-pre-wrap">
            {meldung.beschreibung}
          </div>
        </>
      )}

      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <Info label="Gemeldet von" value={(meldung.gemeldet_von_name ?? '—').replace(/^\[WS\]\s*/, '')} />
        <Info label="Abteilung" value={meldung.abteilung_name ?? '—'} />
        <Info label="Gemeldet am" value={formatDateTime(meldung.gemeldet_am)} />
        {meldung.bearbeitet_am && <Info label="Bearbeitet ab" value={formatDateTime(meldung.bearbeitet_am)} />}
        {meldung.geloest_am &&    <Info label="Gelöst/abgelehnt" value={formatDateTime(meldung.geloest_am)} />}
        {meldung.assigned_to_name && <Info label="Bearbeiter" value={meldung.assigned_to_name} />}
      </div>

      <label className="text-xs font-semibold text-slate-600">Lösung / Bearbeitungsnotiz</label>
      <textarea value={loesung} onChange={e => setLoesung(e.target.value)}
                disabled={meldung.status === 'geloest' || meldung.status === 'abgelehnt'}
                rows={3} className="input w-full mt-1 mb-3 disabled:bg-slate-50"
                placeholder="Was wurde getan? Wie gelöst?" />

      <div className="flex flex-wrap gap-2">
        {meldung.status === 'offen' && (
          <button onClick={() => update({ status: 'in_bearbeitung', loesung })}
                  disabled={saving}
                  className="btn-secondary text-amber-700 border-amber-300 hover:bg-amber-50">
            ▶ In Bearbeitung nehmen
          </button>
        )}
        {(meldung.status === 'offen' || meldung.status === 'in_bearbeitung') && (
          <>
            <button onClick={() => update({ status: 'geloest', loesung })}
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50">
              ✓ Als gelöst markieren
            </button>
            <button onClick={() => update({ status: 'abgelehnt', loesung })}
                    disabled={saving}
                    className="btn-secondary text-slate-600 hover:bg-slate-100">
              Ablehnen
            </button>
          </>
        )}
        {(meldung.status === 'geloest' || meldung.status === 'abgelehnt') && (
          <button onClick={() => update({ status: 'offen' })}
                  disabled={saving}
                  className="btn-secondary text-sky-700 border-sky-300 hover:bg-sky-50">
            ↻ Wieder öffnen
          </button>
        )}
        {loesung !== (meldung.loesung ?? '') && meldung.status === 'in_bearbeitung' && (
          <button onClick={() => update({ loesung })} disabled={saving}
                  className="btn-primary">
            Notiz speichern
          </button>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-slate-50 rounded p-2">
      <div className="text-[10px] uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
function StatusBox({ label, value, tone }: {
  label: string; value: number; tone: 'red'|'amber'|'sky'|'emerald'|'slate';
}) {
  const c = { red: 'bg-red-50 border-red-200 text-red-800', amber: 'bg-amber-50 border-amber-200 text-amber-800',
              sky: 'bg-sky-50 border-sky-200 text-sky-800', emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
              slate: 'bg-slate-50 border-slate-200 text-slate-700' }[tone];
  return (
    <div className={`rounded-lg p-3 border ${c}`}>
      <div className="text-[10px] uppercase font-semibold opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
function formatDateTime(iso: string): string { return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
